import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Upload, Plus, FileText, ChevronDown, ChevronRight, Eye, Download, PenTool, Send, Trash2, AlertCircle } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../../layout/ListFiltr';
import KDoreseniRadek, { KDoreseniSeznam } from '../../layout/KDoreseni';
import SidePanel from '../../SidePanel';
import { SECTION_COLOR, datum, prijmeniJmeno } from '../lide';
import { Tlacitko, Hlaska, PotvrzeniModal, muted, linka } from '../karta/ui';
import { NahratModal, HromadneModal } from './NahravaniModaly';

/**
 * Lidé → Osobní dokumenty.
 *
 * Seznam podle normy (§2, §3): hledání, filtr (zaměstnanec, typ) naší
 * `Dropdown` místo šesti nativních selectů, pohledy s počty, skupiny podle
 * typu se souhrnem podpisů. Ikony akcí z řádků zmizely — klik na dokument
 * otevře **inspektor** nad třetím sloupcem (§1f), kde je náhled, podpisy
 * a akce; trvalé smazání jen tam, jen superadmin, s potvrzením.
 *
 * Třetí sloupec (`panelVzdy`) nese souhrn a „K dořešení" (čeká na podpis).
 * Na mobilu ho nahradí řádek nad seznamem, který otevře modal.
 */

const DEN = 86400000;
const BRZY = 30;

const velikost = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${Math.round(b / 1024)} kB`;
    if (b < 1073741824) return `${(b / 1048576).toFixed(1).replace('.', ',')} MB`;
    return `${(b / 1073741824).toFixed(1).replace('.', ',')} GB`;
};
const dniOd = (v) => (v ? Math.floor((Date.now() - new Date(v)) / DEN) : null);
const dniDo = (v) => (v ? Math.ceil((new Date(v) - Date.now()) / DEN) : null);
const dnu = (n) => `${n} ${n === 1 ? 'den' : n < 5 ? 'dny' : 'dní'}`;

const expirovany = (d) => d.expiruje_at && dniDo(d.expiruje_at) < 0;
const ceka = (d) => d.vyzaduje_podpis && !d.podepsano_at;

const POHLEDY = [
    { id: 'platne', label: 'Platné', test: d => !expirovany(d) },
    { id: 'ceka', label: 'Čeká na podpis', test: d => ceka(d) && !expirovany(d), upozorneni: true },
    { id: 'expiruje', label: 'Expiruje do 30 dní', test: d => d.expiruje_at && dniDo(d.expiruje_at) >= 0 && dniDo(d.expiruje_at) <= BRZY },
    { id: 'expirovane', label: 'Expirované', test: d => expirovany(d) },
];

export default function OsobniDokumenty({
    isDark, isMobile, cardStyle, inputStyle, mutedColor,
    zamestnanci, dokumenty, loading, chyba, onZmena,
    upravujeDokumenty, isSuperadmin, rezervaDole = null,
}) {
    const [hledani, setHledani] = useState('');
    const [pohled, setPohled] = useState('platne');
    const [filtr, setFiltr] = useState({ zamestnanec: '', typ: '' });
    const [sbalene, setSbalene] = useState(() => new Set());
    const [vybranyId, setVybranyId] = useState(null);
    const [modal, setModal] = useState(null);                 // 'nahrat' | 'hromadne'
    const [smazat, setSmazat] = useState(null);
    const [akce, setAkce] = useState({ bezi: null, hlaska: null });

    // Horní hrana dat pro inspektor — měří se řádek se seznamem a panelem (§1f)
    const obsahRef = useRef(null);
    const [inspektorTop, setInspektorTop] = useState(0);
    const zmer = useCallback(() => {
        const el = obsahRef.current;
        if (el) setInspektorTop(Math.max(0, Math.round(el.getBoundingClientRect().top)));
    }, []);
    useEffect(() => {
        zmer();
        window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [zmer, isMobile]);

    const vsechny = dokumenty || [];
    const aktivniPohled = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const jmeno = (d) => (d.zamestnanec ? prijmeniJmeno(d.zamestnanec) : '—');

    const filtrSkupiny = useMemo(() => [
        {
            id: 'zamestnanec', typ: 'dropdown', label: 'Zaměstnanec',
            options: [...new Map(vsechny.filter(d => d.zamestnanec).map(d => [d.zamestnanec_id, { value: d.zamestnanec_id, label: jmeno(d) }])).values()]
                .sort((a, b) => a.label.localeCompare(b.label, 'cs')),
        },
        {
            id: 'typ', typ: 'dropdown', label: 'Typ dokumentu',
            options: [...new Map(vsechny.filter(d => d.typ_dokumentu).map(d => [d.typ_dokumentu.id, { value: d.typ_dokumentu.id, label: d.typ_dokumentu.nazev }])).values()]
                .sort((a, b) => a.label.localeCompare(b.label, 'cs')),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [dokumenty]);

    const vyfiltrovane = useMemo(() => {
        const q = hledani.trim().toLowerCase();
        return vsechny.filter(d => {
            if (!aktivniPohled.test(d)) return false;
            if (filtr.zamestnanec && String(d.zamestnanec_id) !== String(filtr.zamestnanec)) return false;
            if (filtr.typ && String(d.typ_dokumentu?.id) !== String(filtr.typ)) return false;
            if (!q) return true;
            return [d.nazev, d.soubor_nazev, d.zamestnanec?.jmeno, d.zamestnanec?.prijmeni].some(v => (v || '').toLowerCase().includes(q));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dokumenty, hledani, aktivniPohled, filtr]);

    const skupiny = useMemo(() => {
        const m = new Map();
        for (const d of vyfiltrovane) {
            const k = d.typ_dokumentu?.nazev || d.typ || 'Ostatní';
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(d);
        }
        return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, 'cs'))
            .map(([nazev, docs]) => ({ nazev, docs: docs.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)) }));
    }, [vyfiltrovane]);

    const cekajici = vsechny.filter(d => ceka(d) && !expirovany(d)).sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
    const kDoreseni = cekajici.map(d => ({
        id: `dok-${d.id}`, problem: true, druh: 'podpis', dokument: d,
        titulek: `${jmeno(d)} — ${d.nazev}`,
        popis: `nepodepsáno · čeká ${dnu(dniOd(d.uploaded_at) ?? 0)}`,
    }));

    const pripomenout = async (d) => {
        setAkce({ bezi: d.id, hlaska: null });
        let h;
        try {
            const res = await fetch(`/api/dokumenty/${d.id}/pripomenout-podpis`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
            const e = res.ok ? null : await res.json().catch(() => ({}));
            h = res.ok ? { typ: 'ok', text: 'Připomínka odeslána' } : { typ: 'chyba', text: e.error || 'Nepodařilo se odeslat' };
        } catch { h = { typ: 'chyba', text: 'Chyba při odesílání' }; }
        setAkce({ bezi: null, hlaska: { ...h, id: d.id } });
    };

    const podepsat = async (d) => {
        setAkce({ bezi: d.id, hlaska: null });
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        const res = await fetch(`/api/dokumenty/${d.id}/podepsat-zamestnavatel`, { method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': token } });
        const e = res.ok ? null : await res.json().catch(() => ({}));
        setAkce({ bezi: null, hlaska: { id: d.id, typ: res.ok ? 'ok' : 'chyba', text: res.ok ? 'Podepsáno za firmu' : (e.error || 'Podpis selhal') } });
        if (res.ok) onZmena();
    };

    const renderAkce = (p) => (upravujeDokumenty && p.druh === 'podpis' ? (
        <>
            <Tlacitko isDark={isDark} vyska={isMobile ? 40 : 28} disabled={akce.bezi === p.dokument.id} onClick={() => pripomenout(p.dokument)}>
                <Send size={13} /> {akce.bezi === p.dokument.id ? 'Odesílám…' : 'Připomenout'}
            </Tlacitko>
            {akce.hlaska?.id === p.dokument.id && <Hlaska hlaska={akce.hlaska} />}
        </>
    ) : null);

    const vybrany = vybranyId ? vsechny.find(d => d.id === vybranyId) : null;
    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';
    const nastavFiltr = (id, v) => setFiltr(f => ({ ...f, [id]: v || '' }));
    const resetFiltru = () => setFiltr({ zamestnanec: '', typ: '' });
    const grid = { display: 'grid', gridTemplateColumns: 'minmax(240px, 2.2fr) minmax(140px, 1.2fr) 80px 100px 130px 170px', columnGap: 14, alignItems: 'center' };

    const seznam = ({ siroky }) => (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat dokument nebo zaměstnance…', placeholderKratky: 'Hledat' }}
                akce={upravujeDokumenty ? [
                    { id: 'nahrat', label: 'Nahrát', Icon: Plus, onClick: () => setModal('nahrat') },
                    ...(isMobile ? [] : [{ id: 'hromadne', label: 'Hromadné nahrání', Icon: Upload, onClick: () => setModal('hromadne'), primarni: true }]),
                ] : []}
                filtr={(
                    <FiltrTlacitko skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={resetFiltru}
                        pocetVysledku={vyfiltrovane.length} siroky={siroky} kompaktni={isMobile}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
                pod={isMobile && kDoreseni.length > 0 ? (
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} renderAkce={renderAkce}
                        titulek={`${kDoreseni.length} ${kDoreseni.length === 1 ? 'dokument čeká' : kDoreseni.length < 5 ? 'dokumenty čekají' : 'dokumentů čeká'} na podpis`} />
                ) : null}
                pohled={POHLEDY.map(p => {
                    const n = vsechny.filter(p.test).length;
                    return (
                        <PohledPill key={p.id} label={p.label} pocet={n} aktivni={pohled === p.id} upozorneni={p.upozorneni && n > 0}
                            onClick={() => setPohled(p.id)} barva={SECTION_COLOR} isDark={isDark} mutedColor={mutedColor} kompaktni={isMobile} />
                    );
                })}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={resetFiltru}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />

            {chyba && (
                <div className="flex items-center" style={{ gap: 10, margin: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    <AlertCircle size={15} style={{ color: '#f87171' }} />
                    <span className="text-[13px]" style={{ color: '#f87171', flex: 1 }}>Dokumenty se nepodařilo načíst: {chyba}</span>
                    <Tlacitko isDark={isDark} onClick={onZmena}>Zkusit znovu</Tlacitko>
                </div>
            )}

            {siroky && vyfiltrovane.length > 0 && (
                <div style={{ ...grid, padding: '10px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', color: mutedColor, textTransform: 'uppercase', flexShrink: 0 }}>
                    <span>Dokument</span><span>Zaměstnanec</span><span style={{ textAlign: 'right' }}>Velikost</span><span>Nahráno</span><span>Platnost</span><span>Podpis</span>
                </div>
            )}

            <div className="overflow-y-auto" style={{ flex: 1, minHeight: 0, borderTop: siroky ? `1px solid ${hranice}` : 'none', paddingBottom: rezervaDole || undefined }}>
                {loading ? (
                    <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Načítám…</p>
                ) : vyfiltrovane.length === 0 ? (
                    <p className="text-center text-xs py-10" style={{ color: mutedColor }}>
                        {hledani.trim() ? `Nic nenalezeno pro „${hledani}"` : 'V tomhle pohledu nic není.'}
                    </p>
                ) : skupiny.map(sk => {
                    const sbalena = sbalene.has(sk.nazev);
                    const cekaVeSkupine = sk.docs.filter(ceka).length;
                    const Sipka = sbalena ? ChevronRight : ChevronDown;
                    return (
                        <div key={sk.nazev}>
                            <button type="button" onClick={() => setSbalene(s => { const n = new Set(s); n.has(sk.nazev) ? n.delete(sk.nazev) : n.add(sk.nazev); return n; })}
                                className="w-full text-left"
                                style={{
                                    ...(siroky ? grid : { display: 'flex', alignItems: 'center', gap: 8 }),
                                    padding: '0 14px', minHeight: isMobile ? 44 : 38, border: 'none', borderBottom: `1px solid ${hranice}`,
                                    background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)', cursor: 'pointer', fontSize: 12.5, color: mutedColor,
                                }}>
                                <span className="flex items-center min-w-0" style={{ gap: 8, color: 'var(--text-primary)' }}>
                                    <Sipka size={15} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                                    <span className="truncate font-semibold">{sk.nazev}</span>
                                    <span style={{ color: mutedColor, fontWeight: 500 }}>{sk.docs.length}</span>
                                </span>
                                {siroky ? (<><span /><span /><span /><span /></>) : null}
                                <span style={{ marginLeft: siroky ? 0 : 'auto', color: cekaVeSkupine ? '#f87171' : mutedColor, fontWeight: 600 }}>
                                    {cekaVeSkupine ? `${cekaVeSkupine} ${cekaVeSkupine === 1 ? 'čeká' : 'čekají'}` : ''}
                                </span>
                            </button>
                            {!sbalena && sk.docs.map(d => (
                                siroky
                                    ? <RadekMrizka key={d.id} d={d} grid={grid} aktivni={vybranyId === d.id} onClick={() => setVybranyId(d.id)} jmeno={jmeno(d)} isDark={isDark} mutedColor={mutedColor} hranice={hranice} />
                                    : <RadekUzky key={d.id} d={d} aktivni={vybranyId === d.id} onClick={() => setVybranyId(d.id)} jmeno={jmeno(d)} kompaktni={isMobile} mutedColor={mutedColor} hranice={hranice} />
                            ))}
                        </div>
                    );
                })}
            </div>

            {!loading && vyfiltrovane.length > 0 && !isMobile && (
                <div className="px-3 py-2 text-[10.5px]" style={{ borderTop: `1px solid ${hranice}`, color: mutedColor, flexShrink: 0 }}>
                    {vyfiltrovane.length} {vyfiltrovane.length === 1 ? 'dokument' : vyfiltrovane.length < 5 ? 'dokumenty' : 'dokumentů'} · {velikost(vyfiltrovane.reduce((s, d) => s + (d.soubor_velikost || 0), 0))}
                </div>
            )}
        </div>
    );

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: '14px 16px' }}>
            <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em', marginBottom: 6 }}>Dokumenty</p>
            {[
                ['Celkem', vsechny.length],
                ['Velikost', velikost(vsechny.reduce((s, d) => s + (d.soubor_velikost || 0), 0))],
                ['Čeká na podpis', cekajici.length],
                ['Expiruje do 30 dní', vsechny.filter(POHLEDY[2].test).length],
            ].map(([popis, hodnota]) => (
                <div key={popis} className="flex items-center justify-between" style={{ minHeight: 32, borderBottom: `1px solid ${linka(isDark)}` }}>
                    <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>{popis}</span>
                    <span className="text-[12.5px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{hodnota}</span>
                </div>
            ))}
            <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em', margin: '18px 0 6px' }}>K dořešení</p>
            <KDoreseniSeznam polozky={kDoreseni.slice(0, 6)} isDark={isDark} renderAkce={renderAkce} prazdne="Vše podepsáno." />
            {kDoreseni.length > 6 && (
                <button type="button" onClick={() => setPohled('ceka')} className="text-[12px] font-semibold"
                    style={{ marginTop: 8, background: 'none', border: 'none', color: SECTION_COLOR, cursor: 'pointer', padding: 0 }}>
                    a dalších {kDoreseni.length - 6} → pohled Čeká na podpis
                </button>
            )}
        </div>
    );

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={false} mobilRezim mobilPanel={false} panelVzdy
                barva={SECTION_COLOR} isDark={isDark} seznam={seznam} panel={panel} />

            {vybrany && (
                <SidePanel open onClose={() => setVybranyId(null)} isDark={isDark} isMobile={isMobile}
                    width={336} modal={false} accent={SECTION_COLOR} topOffset={inspektorTop}
                    title={vybrany.nazev}>
                    <DetailDokumentu d={vybrany} jmeno={jmeno(vybrany)} isDark={isDark} isMobile={isMobile}
                        upravuje={upravujeDokumenty} isSuperadmin={isSuperadmin} akce={akce}
                        onPripomenout={() => pripomenout(vybrany)} onPodepsat={() => podepsat(vybrany)}
                        onSmazat={() => setSmazat(vybrany)} />
                </SidePanel>
            )}

            {modal === 'nahrat' && <NahratModal zamestnanci={zamestnanci} isDark={isDark} isMobile={isMobile} onZavri={() => setModal(null)} onHotovo={onZmena} />}
            {modal === 'hromadne' && <HromadneModal zamestnanci={zamestnanci} isDark={isDark} isMobile={isMobile} onZavri={() => setModal(null)} onHotovo={onZmena} />}

            {smazat && (
                <PotvrzeniModal isDark={isDark} onZavri={() => setSmazat(null)} nadpis="Trvale smazat dokument?"
                    text={`„${smazat.nazev}" (${jmeno(smazat)}) se smaže i se souborem. Nejde to vrátit.`}
                    onPotvrdit={async () => {
                        const res = await fetch(`/api/dokumenty/${smazat.id}`, { method: 'DELETE', headers: { Accept: 'application/json' } });
                        if (res.ok) { setVybranyId(null); onZmena(); }
                        else { const e = await res.json().catch(() => ({})); setAkce({ bezi: null, hlaska: { id: smazat.id, typ: 'chyba', text: e.error || 'Nepodařilo se smazat' } }); }
                        setSmazat(null);
                    }} />
            )}
        </div>
    );
}

function Podpis({ d, mutedColor }) {
    if (!d.vyzaduje_podpis) return <span style={{ color: mutedColor }}>nevyžaduje</span>;
    if (d.podepsano_at) return <span style={{ color: 'var(--text-secondary)' }}>podepsáno {datum(d.podepsano_at)}</span>;
    return <span style={{ color: '#f87171', fontWeight: 600 }}>čeká {dnu(dniOd(d.uploaded_at) ?? 0)}</span>;
}

function Platnost({ d, mutedColor }) {
    if (!d.expiruje_at) return <span style={{ color: mutedColor }}>—</span>;
    const n = dniDo(d.expiruje_at);
    if (n < 0) return <span style={{ color: '#f87171', fontWeight: 600 }}>expiroval {datum(d.expiruje_at)}</span>;
    if (n <= BRZY) return <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>za {dnu(n)}</span>;
    return <span>do {datum(d.expiruje_at)}</span>;
}

function RadekMrizka({ d, grid, aktivni, onClick, jmeno, isDark, mutedColor, hranice }) {
    return (
        <button type="button" onClick={onClick} className="w-full text-left"
            style={{
                ...grid, padding: '0 14px', minHeight: 46, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)',
            }}>
            <span className="flex items-center min-w-0" style={{ gap: 10 }}>
                <FileText size={16} style={{ color: mutedColor, flexShrink: 0 }} />
                <span className="truncate font-semibold" style={{ color: 'var(--text-primary)' }}>{d.nazev}</span>
            </span>
            <span className="truncate">{jmeno}</span>
            <span className="tabular-nums" style={{ textAlign: 'right' }}>{velikost(d.soubor_velikost)}</span>
            <span className="tabular-nums">{datum(d.uploaded_at)}</span>
            <span className="truncate"><Platnost d={d} mutedColor={mutedColor} /></span>
            <span className="truncate"><Podpis d={d} mutedColor={mutedColor} /></span>
        </button>
    );
}

function RadekUzky({ d, aktivni, onClick, jmeno, kompaktni, mutedColor, hranice }) {
    return (
        <button type="button" onClick={onClick} className="w-full text-left flex items-center"
            style={{
                gap: 12, padding: '8px 14px', minHeight: kompaktni ? 60 : 52, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer',
            }}>
            <FileText size={kompaktni ? 18 : 16} style={{ color: mutedColor, flexShrink: 0 }} />
            <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold" style={{ fontSize: kompaktni ? 15 : 13, color: 'var(--text-primary)' }}>{d.nazev}</span>
                <span className="block truncate" style={{ fontSize: kompaktni ? 12.5 : 11.5, color: mutedColor }}>
                    {jmeno} · {datum(d.uploaded_at)}{d.vyzaduje_podpis ? ' · ' : ''}{d.vyzaduje_podpis && <Podpis d={d} mutedColor={mutedColor} />}
                </span>
            </span>
        </button>
    );
}

function DetailDokumentu({ d, jmeno, isDark, isMobile, upravuje, isSuperadmin, akce, onPripomenout, onPodepsat, onSmazat }) {
    const mut = muted(isDark);
    const radek = (popis, hodnota) => (
        <div className="flex items-center justify-between" style={{ minHeight: isMobile ? 44 : 34, gap: 10, borderBottom: `1px solid ${linka(isDark)}` }}>
            <span className="text-[12.5px]" style={{ color: mut }}>{popis}</span>
            <span className="text-[12.5px] font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{hodnota}</span>
        </div>
    );
    const vyska = isMobile ? 44 : 34;
    return (
        <div style={{ padding: 16 }}>
            {radek('Zaměstnanec', jmeno)}
            {radek('Typ', d.typ_dokumentu?.nazev || 'Ostatní')}
            {radek('Soubor', `${d.soubor_nazev || '—'} · ${velikost(d.soubor_velikost)}`)}
            {radek('Nahráno', datum(d.uploaded_at) || '—')}
            {radek('Platnost', <Platnost d={d} mutedColor={mut} />)}
            {d.vyzaduje_podpis && radek('Podpis zaměstnance', <Podpis d={d} mutedColor={mut} />)}
            {d.vyzaduje_podpis && radek('Podpis firmy', d.podpis_zamestnavatel_at ? `podepsáno ${datum(d.podpis_zamestnavatel_at)}` : <span style={{ color: mut }}>zatím ne</span>)}
            {d.popis && <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)', marginTop: 10 }}>{d.popis}</p>}

            <div className="flex flex-wrap" style={{ gap: 8, marginTop: 16 }}>
                {upravuje && ceka(d) && (
                    <Tlacitko isDark={isDark} primarni vyska={vyska} onClick={onPripomenout} disabled={akce.bezi === d.id}>
                        <Send size={14} /> {akce.bezi === d.id ? 'Odesílám…' : 'Připomenout podpis'}
                    </Tlacitko>
                )}
                {d.soubor_mime === 'application/pdf' && <Tlacitko isDark={isDark} vyska={vyska} href={`/api/dokumenty/${d.id}/preview`}><Eye size={14} /> Náhled</Tlacitko>}
                <Tlacitko isDark={isDark} vyska={vyska} href={`/api/dokumenty/${d.id}/download`}><Download size={14} /> Stáhnout</Tlacitko>
                {upravuje && d.vyzaduje_podpis && !d.podpis_zamestnavatel_at && d.soubor_mime === 'application/pdf' && (
                    <Tlacitko isDark={isDark} vyska={vyska} onClick={onPodepsat} disabled={akce.bezi === d.id}><PenTool size={14} /> Podepsat za firmu</Tlacitko>
                )}
            </div>
            {akce.hlaska?.id === d.id && <Hlaska hlaska={akce.hlaska} />}

            {isSuperadmin && !isMobile && (
                <div style={{ marginTop: 22, paddingTop: 12, borderTop: `1px solid ${linka(isDark)}` }}>
                    <Tlacitko isDark={isDark} nebezpecne onClick={onSmazat}><Trash2 size={14} /> Trvale smazat…</Tlacitko>
                </div>
            )}
        </div>
    );
}
